import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import * as RouterPath from '../../data/RouterPath';
import QRCode from 'qrcode.react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useLanguageStore } from '../../store/useLanguageStore';
import { getExpiryDate, setCookie } from '../../utils/cookie';
import { useCountryStore } from '../../store/useCountryStore';
import { useRegionStore } from '../../store/useRegionStore';

function AppLogin() {
  const navigate = useNavigate();
  const ws = useRef<WebSocket | null>(null);
  const [qr, setQr] = useState('');
  const [pin, setPin] = useState('');
  const [time, setTime] = useState(0);
  const { countryCode } = useCountryStore();
  const { languagePack } = useLanguageStore();
  const region = useRegionStore((s) => s.region);
  const [hydrated, setHydrated] = useState(
    typeof useRegionStore.persist?.hasHydrated === 'function'
      ? useRegionStore.persist.hasHydrated()
      : true
  );

  useEffect(() => {
    if (!useRegionStore.persist) return;
    const unsub = useRegionStore.persist.onFinishHydration?.(() => {
      setHydrated(true);
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  const countryCodeStore = useCountryStore((s) => s.countryCode);

  useEffect(() => {
    if (!hydrated) return;
    console.log('countryCode:', countryCodeStore);
    console.log('region:', region ?? 'KIC');
  }, [countryCodeStore, region, hydrated]);

  type OAuthHeaders = Record<string, string>;
  type OAuthParams = {
    code: string;
    redirect_uri: string;
    grant_type?: 'authorization_code';
  };

  const getAppCode = () => {
    const socket = ws.current;
    if (!socket) return;
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          action: 'qr-login',
          country_code: countryCodeStore,
          cross_login: '',
          except_new: '',
          show_login_type: '',
          svc_list: '',
          terms_name: '',
          third_party_service_name: '',
          language: 'ko-KR',
          login_save: true,
          response_type: 'code',
          client_id: process.env.REACT_APP_CLOUDEMP_CLIENT_ID,
          redirect_uri: 'https://httpapis-thinqweb.lgthinq.com/authorize',
          state: 'aa123',
        })
      );
    }
  };

  const getTokenByCode = async (
    signature: string,
    date: string,
    path: string,
    uri: string,
    code: string,
    baseHeaders: OAuthHeaders
  ) => {
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: uri,
    });

    const headers = {
      ...baseHeaders,
      'x-lge-oauth-signature': signature,
      'x-lge-oauth-date': date,
    };

    const response = await axios.post(
      `https://${countryCodeStore.toLowerCase()}.${process.env.REACT_APP_EMP_AUTH_URI}${path}`,
      params,
      { headers }
    );
    return response;
  };

  const get_oauth_signature = async (
    path: string,
    params: OAuthParams,
    headers: OAuthHeaders
  ) => {
    try {
      const datetimeRes = await axios.get(
        `https://${countryCodeStore.toLowerCase()}.${process.env.REACT_APP_EMP_AUTH_URI}/oauth/1.0/datetime`,
        {
          headers: headers,
        }
      );

      const date: string | undefined = datetimeRes?.data?.date;
      if (!date) throw new Error('Failed to get EMP datetime');

      const uri = encodeURIComponent(params.redirect_uri);
      const stringToSign = `${path}?code=${params.code}&grant_type=authorization_code&redirect_uri=${uri}`;
      const key = CryptoJS.enc.Utf8.parse(
        process.env.REACT_APP_EMP_CLIENT_SECRET
      );
      const data = CryptoJS.enc.Utf8.parse(stringToSign + `\n${date}`);
      const hashed = CryptoJS.HmacSHA1(data, key);
      const signature = CryptoJS.enc.Base64.stringify(hashed);
      const token = getTokenByCode(
        signature,
        date,
        stringToSign,
        params.redirect_uri,
        params.code,
        headers
      );
      return token;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  useEffect(() => {
    const socket_url = process.env.REACT_APP_CLOUDEMP_SOCKET_SERVER as
      | string
      | undefined;
    if (!socket_url) {
      console.error('Missing REACT_APP_CLOUDEMP_SOCKET_SERVER');
      return;
    }
    const socket = new WebSocket(socket_url);
    ws.current = socket;

    socket.onopen = () => {
      getAppCode();
    };

    socket.onmessage = async (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (Object.prototype.hasOwnProperty.call(msg, 'code')) {
          const path = '/oauth/1.0/oauth2/token';
          const headers: OAuthHeaders = {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-lge-appkey': process.env.REACT_APP_EMP_CLIENT_KEY as string,
          };
          const params: OAuthParams = {
            code: msg.code,
            redirect_uri: msg.redirect_uri,
          };

          const res = await get_oauth_signature(path, params, headers);

          setCookie('bearer_token', res.data.access_token, {
            path: '/',
            secure: '/',
            expires: getExpiryDate(2),
          });
          navigate(RouterPath.TOKENS, {
            state: {
              type: 'login',
              emp: res.data,
            },
          });
        } else if (
          Object.prototype.hasOwnProperty.call(msg, 'qrCode') &&
          Object.prototype.hasOwnProperty.call(msg, 'pinCode')
        ) {
          setPin(msg.pinCode);
          setQr(msg.qrCode);
          setTime(Number(msg.ttl) || 0);
        }
      } catch (e) {
        console.error('WebSocket message handling error:', e);
      }
    };

    return () => {
      try {
        socket.close();
      } catch {
        /* empty */
      }
      ws.current = null;
    };
  }, [navigate]);

  useEffect(() => {
    if (time <= 0) {
      setQr('');
      setPin('');
      setTime(0);
      getAppCode();
      return;
    }

    const timerId = setInterval(() => {
      setTime((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [time]);

  return (
    <Box className="main login" component="main">
      <Box className="main-header">
        <IconButton size="large" edge="start" href="/login">
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h3" className="tit">
          {languagePack && languagePack.appLogin.title}
        </Typography>
      </Box>
      <Box className="main-body">
        <Typography variant="body1" className="desc">
          {languagePack && languagePack.appLogin.description1}
          <br />
          <br />
          {languagePack && languagePack.appLogin.description2}
        </Typography>
        <Box className="view">
          <Box className="view-qr">
            {qr ? (
              <>
                <div>
                  <QRCode value={qr} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '1rem',
                    }}
                  >
                    PIN: {pin}
                  </div>
                </div>
              </>
            ) : (
              <>
                <CircularProgress />
              </>
            )}
          </Box>
          <Typography variant="caption" className="view-time">
            {languagePack && languagePack.appLogin.remainTime}
            <strong className="blue">{time}</strong> 초
          </Typography>
        </Box>
      </Box>
      {/* <Box className="main-footer">
        <Link href="/PinLogin" className="shortcut">
          <img src={icPin} alt="Pin Login Shortcut Icon" />
          <span>일회용 번호로 전환하기</span>
        </Link>
      </Box> */}
    </Box>

    // <Box className="main login" component="main">
    //   <Box className="main-header">
    //     <IconButton size="large" edge="start" onClick={() => navigate(RouterPath.TOKENS)}>
    //       <ArrowBackIosIcon />
    //     </IconButton>
    //     <Typography variant="h3" className="tit">
    //       ThinQ App으로 로그인
    //     </Typography>
    //   </Box>
    //   <Box className="main-box">
    //     <Card className="main-card">
    //       <CardContent>
    //         <Box className="main-body">
    //           <Typography variant="body1" className="desc">
    //             모바일 기기에서 설치된 LG ThinQ 앱을 열고, 앱 설정 &#62; “ThinQ 계정 공유” 메뉴를 선택해 주세요.
    //             <br />
    //             <br />
    //             화면에 표시된 QR코드를 인식해 주세요.
    //           </Typography>
    //           <br />
    //           <Box className="view">
    //             <Box className="view-qr">
    //               {qr ? (
    //                 <>
    //                   <QRCode value={qr} alt="LG ThinQ App QR Code" />
    //                 </>
    //               ) : (
    //                 <>
    //                   <CircularProgress />
    //                 </>
    //               )}
    //             </Box>
    //           </Box>
    //         </Box>
    //       </CardContent>
    //     </Card>
    //     {/*<Card className='main-card'>*/}
    //     {/*	<CardContent>*/}
    //     {/*		<Box className='main-body'>*/}
    //     {/*			<Typography variant="body1" className='desc'>*/}
    //     {/*				모바일 기기에서 설치된 LG ThinQ 앱을 열고, 앱 설정 &#62; “ThinQ 계정 공유” 메뉴를 선택하여 일회용 로그인 번호를 입력해 주세요.*/}
    //     {/*			</Typography>*/}
    //     {/*			<Box className='view'>*/}
    //     {/*				<Stack className='view-num' direction="row" spacing={2}>*/}
    //     {/*					{*/}
    //     {/*						pin ? <>*/}
    //     {/*							<span>{pin[0]}</span>*/}
    //     {/*							<span>{pin[1]}</span>*/}
    //     {/*							<span>{pin[2]}</span>*/}
    //     {/*							<span>{pin[3]}</span>*/}
    //     {/*							<span>{pin[4]}</span>*/}
    //     {/*							<span>{pin[5]}</span>*/}
    //     {/*							<span>{pin[6]}</span>*/}
    //     {/*						</>*/}
    //     {/*						: <>*/}
    //     {/*							<CircularProgress />*/}
    //     {/*						</>*/}
    //     {/*					}*/}
    //     {/*				</Stack>*/}
    //     {/*			</Box>*/}
    //     {/*		</Box>*/}
    //     {/*	</CardContent>*/}
    //     {/*</Card>*/}
    //   </Box>
    //   <Box className="footer-body">
    //     <Typography variant="caption" className="view-time">
    //       {time ? (
    //         <>
    //           남은 시간 <strong className="blue">{time}</strong> 초
    //         </>
    //       ) : (
    //         <>
    //           <Button onClick={getAppCode}>Refresh</Button>
    //         </>
    //       )}
    //     </Typography>
    //   </Box>
    // </Box>
  );
}

export default AppLogin;
